export const CHUNK_RELOAD_FLAG = 'roger:chunk-reload-attempted';
export const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

let reloadPending = false;

/** True once the guard has called reload, until the page unloads. */
export const isChunkReloadPending = () => reloadPending;

/**
 * After a deploy, an open tab may request a chunk that no longer exists. Vite
 * fires `vite:preloadError`; a stale tab reloads to pick up the new build.
 *
 * The sessionStorage flag holds the time of the last reload. The tab reloads
 * at most once per cooldown, so a real outage or a broken build cannot cause
 * a reload loop. The flag expires on its own, so a tab whose reload landed on
 * the same build (a network blip) can still reload on the next deploy. We do
 * not reset it on page load: many chunks load late, so that would loop.
 *
 * When the browser is offline a reload only shows the browser's offline page,
 * so the tab stays on the error fallback instead.
 *
 * We do not call preventDefault. Vite then rethrows the real chunk error to
 * the error boundary. Preventing default would make React.lazy fail with a
 * misleading "reading 'default'" TypeError instead. Sentry drops events while
 * the reload is pending (see `beforeSend` in sentry.ts), so a tab that
 * recovers does not report the error.
 *
 * If storage is unavailable we cannot guard against a loop, so we never reload.
 */
export function installChunkReloadGuard(): () => void {
  const onPreloadError = () => {
    if (!navigator.onLine) return;
    try {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_FLAG));
      if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return;
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
    } catch {
      return;
    }
    reloadPending = true;
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
